import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, FileText, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { generateStatementPDF } from '../lib/statementPdf';
import { CompanyContext } from '../contexts/CompanyContext';

const PARTY_TYPES = [
  { v: 'tenant', l: 'Tenant' },
  { v: 'supplier', l: 'Supplier' },
  { v: 'client', l: 'Client' },
];

export default function Statements() {
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const navigate = useNavigate();
  const [partyType, setPartyType] = useState('tenant');
  const [parties, setParties] = useState([]);
  const [partyIdOrName, setPartyIdOrName] = useState('');
  const [statement, setStatement] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const canRead = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR'].includes(user?.role);

  const loadParties = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/statements-parties/${partyType}`, { withCredentials: true });
      setParties(r.data || []);
      // reset selection if it doesn't exist in new list
      if (!(r.data || []).find(p => (p.id || p.name) === partyIdOrName)) {
        setPartyIdOrName('');
        setStatement(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load parties');
    }
  }, [partyType, partyIdOrName]);
  useEffect(() => { if (canRead) loadParties(); }, [canRead, loadParties]);

  const fetchStatement = async () => {
    if (!partyIdOrName) { toast.error('Choose a party'); return; }
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/statements/${partyType}/${encodeURIComponent(partyIdOrName)}`,
        { params, withCredentials: true });
      setStatement(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load statement');
    }
    setLoading(false);
  };

  const openLink = (entry) => {
    const mod = entry.link?.module;
    const id = entry.link?.id;
    if (!mod || !id) return;
    if (mod === 'inspections') navigate(`/inspections/${id}`);
    else if (mod === 'credit-debit-notes') navigate('/credit-debit-notes');
    else if (mod === 'invoices' || mod === 'bills' || mod === 'payments') navigate('/accounting');
  };

  const downloadPdf = (viewOnly = false) => {
    if (!statement) return;
    try {
      generateStatementPDF({ statement, company, dateFrom, dateTo, view: viewOnly });
    } catch (e) {
      toast.error('PDF generation failed');
    }
  };

  if (!canRead) {
    return <Layout><div className="p-6">You don&apos;t have access to statements.</div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="statements-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-amber-500" /> Account Statements
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Per-party ledger showing every charge, payment, credit/debit note adjustment, and approved snag deduction — with reference to the source document.
            </p>
          </div>
          {statement && (
            <div className="flex gap-2">
              <Button data-testid="view-pdf-btn" onClick={() => downloadPdf(true)} variant="outline">
                <FileText className="w-4 h-4 mr-1" /> View PDF
              </Button>
              <Button data-testid="download-pdf-btn" onClick={() => downloadPdf(false)} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <FileDown className="w-4 h-4 mr-1" /> Download PDF
              </Button>
            </div>
          )}
        </div>

        <Card className="p-4 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label>Party Type</Label>
              <Select value={partyType} onValueChange={(v) => { setPartyType(v); setPartyIdOrName(''); setStatement(null); }}>
                <SelectTrigger data-testid="party-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PARTY_TYPES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Party</Label>
              <Select value={partyIdOrName} onValueChange={setPartyIdOrName}>
                <SelectTrigger data-testid="party-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {parties.length === 0 && <SelectItem value="__none" disabled>No {partyType}s found</SelectItem>}
                  {parties.map(p => (
                    <SelectItem key={p.id || p.name} value={p.id || p.name}>
                      {p.name}{p.unit_number ? ` (${p.unit_number})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button data-testid="fetch-statement-btn" onClick={fetchStatement} disabled={!partyIdOrName || loading} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              {loading ? 'Loading…' : 'View Statement'}
            </Button>
          </div>
        </Card>

        {statement && (
          <Card className="p-6 print:shadow-none print:border-0" data-testid="statement-view">
            <div className="mb-4 flex flex-wrap justify-between items-start gap-3">
              <div>
                <h2 className="text-xl font-bold">Statement of Account</h2>
                <div className="text-sm text-muted-foreground capitalize">{statement.party_type}</div>
                <div className="text-lg font-semibold" data-testid="statement-party-name">{statement.party_name}</div>
                {(dateFrom || dateTo) && (
                  <div className="text-xs text-muted-foreground">Period: {dateFrom || '—'} to {dateTo || '—'}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase text-muted-foreground">Closing Balance</div>
                <div className={`text-2xl font-bold font-mono ${statement.totals.balance > 0 ? 'text-rose-700' : statement.totals.balance < 0 ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {statement.currency} {Number(statement.totals.balance).toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {statement.totals.balance > 0
                    ? (statement.party_type === 'supplier' ? 'Payable to supplier' : 'Receivable from ' + statement.party_type)
                    : statement.totals.balance < 0
                      ? (statement.party_type === 'supplier' ? 'Advance / credit balance' : 'Credit balance owed to ' + statement.party_type)
                      : 'Settled'}
                </div>
              </div>
            </div>

            {statement.entries.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground border rounded-lg">
                <FileText className="w-10 h-10 mx-auto opacity-40 mb-2" />
                No activity for this party in the selected period.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left w-24">Date</th>
                      <th className="px-3 py-2 text-left w-36">Type</th>
                      <th className="px-3 py-2 text-left w-36">Reference</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right w-28">Debit</th>
                      <th className="px-3 py-2 text-right w-28">Credit</th>
                      <th className="px-3 py-2 text-right w-32">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.entries.map((e, idx) => (
                      <tr key={idx} data-testid={`stmt-row-${idx}`} className="border-t">
                        <td className="px-3 py-2 text-xs">{e.date}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                            e.type.includes('Credit Note') ? 'bg-emerald-100 text-emerald-800' :
                              e.type.includes('Debit Note') ? 'bg-rose-100 text-rose-800' :
                                e.type === 'Payment Received' || e.type === 'Payment Sent' ? 'bg-sky-100 text-sky-800' :
                                  e.type === 'Snag Deduction' ? 'bg-amber-100 text-amber-800' :
                                    'bg-slate-100 text-slate-700'
                          }`}>{e.type}</span>
                        </td>
                        <td className="px-3 py-2">
                          <button type="button" onClick={() => openLink(e)}
                            className="font-mono text-xs text-amber-700 hover:underline">
                            {e.reference || '—'}
                          </button>
                          {e.po_ref && <div className="text-[10px] text-muted-foreground">PO {e.po_ref}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">{e.description}</td>
                        <td className="px-3 py-2 text-right font-mono">{e.debit ? Number(e.debit).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{e.credit ? Number(e.credit).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{Number(e.balance).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 dark:bg-slate-800/40 text-xs font-bold">
                    <tr className="border-t-2">
                      <td colSpan={4} className="px-3 py-2 text-right uppercase">Totals</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(statement.totals.debit).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(statement.totals.credit).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(statement.totals.balance).toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div className="mt-4 text-xs text-muted-foreground">
              Reference links open the source document (invoice / LPO / inspection / note). Approved snag deductions are automatically pulled from inspection reports where this tenant is the counter-party. Only approved credit / debit notes appear here.
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
